'use strict';

import { Packaging } from '@/data/api/packaging/packaging';
import { rowsToOrderlines } from '@/helpers/checkout';
import { Emittable } from '@/data/productlist/generic.ts';
import { getDeliveryTitle } from '@/helpers';
import _ from 'lodash';
import md5 from 'md5';
import { useAccountStore } from '@/stores/account';
import { nextTick } from 'vue';

export class PackagingEmittable extends Emittable {
  cancel: any;
  previousHash: string;
  pack?: Packaging;

  constructor() {
    super();
    const self = this;
    this.previousHash = '';
    this.mixin = {
      data() {
        return {
          c_packaging: [],
          c_packaging_loading: true,
        };
      },
      watch: {
        p_c_rows: {
          deep: true,
          handler(rows) {
            self._getPackaging.call(self, this, rows);
          },
        },
      },
    };
  }

  init() {
    this.previousHash = '';
    this.on('update', this.update.bind(this));
  }

  update(_vm, event) {
    if (['amount', 'unit'].includes(event.key)) {
      _vm.c_packaging_loading = true;
    }
  }

  modify(vm, rows) {
    if (!rows.length) {
      return rows;
    }
    const packaging = vm.c_packaging.map((p) => {
      return {
        id: p.itemNumber,
        artnr: p.itemNumber,
        amount: !vm.c_packaging_loading ? p.quantity : '...',
        unit: !vm.c_packaging_loading ? p.unit : ' ',
        weight: p.weight,
        article: {
          descriptionExt: getDeliveryTitle(p),
          ArtNr: p.itemNumber,
        },
        _meta: {
          addon: true,
          readonly: true,
          addon_type: 'pallet',
          loading: vm.c_packaging_loading,
        },
      };
    });
    rows.push(...packaging);
    return rows;
  }

  mapOrderLines(rows, rowMeasurements = {}) {
    return rowsToOrderlines(rows.filter((item) => item?._meta?.addon_type != 'pallet'))
      .map((item) => {
        if (!item.lineNumber) {
          // Might be a bad way to handle this edge case, but generally it should not happen
          console.warn('lineNumber is missing', item);
          item.lineNumber = '';
        }
        return {
          lineNumber: item.lineNumber,
          lineNumberPosition: item.lineNumberPosition,
          itemNumber: item.itemNumber,
          quantity: item.salesUnitQuantity,
          unit: item.salesUnit,
          userDefinedHeight: rowMeasurements?.[item.lineNumber]?.height,
          userDefinedLength: rowMeasurements?.[item.lineNumber]?.length,
          userDefinedWidth: rowMeasurements?.[item.lineNumber]?.width,
          userDefinedWeight: rowMeasurements?.[item.lineNumber]?.weight,
        };
      })
      .filter((item) => Object.keys(item).length > 0);
  }

  _forceRecompute = _.debounce(function (vm) {
    vm.forceRecompute();
  }, 100);

  getPackagingHandler(warehouse: string) {
    const { region } = useAccountStore();

    // If 'pack' is already set and the region is the same, re-use it
    // Otherwise, create a new instance of Packaging
    if (this.pack) {
      if (this.pack.getRegion() !== region) this.pack.setRegion(region);
      if (this.pack.getWarehouse() !== warehouse) this.pack.setWarehouse(warehouse);
      return this.pack;
    }

    this.pack = new Packaging(import.meta.env.VITE_APP_PRODUCT_API_BASE_URL + '/orders/packaging', {
      region,
      warehouse,
      cache: 60 * 1000,
    });
    return this.pack;
  }

  _getPackaging = _.debounce(this.getPackaging, 500);
  async getPackaging(vm, rows) {
    const deferred = () => {
      vm.c_packaging_loading = false;
    };

    if (!rows.length) {
      return deferred();
    }
    const warehouse = vm.c_selected_warehouse?.id ?? vm.c_selected_warehouse;
    if (!warehouse) {
      console.warn('No warehouse selected for packaging');
      return deferred();
    }

    const { region } = useAccountStore();
    const orderLines = this.mapOrderLines(rows);
    const hash = md5(JSON.stringify(orderLines) + region);
    if (this.previousHash === hash) {
      return deferred();
    }

    this.previousHash = hash;
    vm.c_packaging_loading = true;
    this.getPackagingHandler(warehouse)
      .fetch(orderLines)
      .then((res) => {
        const packaging = res.orderPackagingResponsePackageLines;
        vm.c_packaging = packaging;
      })
      .catch((err) => {
        if (err !== 'cancelled') {
          console.error('Error fetching packaging', err);
        }
      })
      .finally(() => {
        deferred();
        nextTick(() => {
          this._forceRecompute.call(this, vm);
        });
      });
  }
}
